import path from "ramda/es/path";
import T from "ramda/es/T";
import allPass from "ramda/es/allPass";
import always from "ramda/es/always";
import compose from "ramda/es/compose";
import cond from "ramda/es/cond";
import equals from "ramda/es/equals";
import filter from "ramda/es/filter";
import flatten from "ramda/es/flatten";
import identity from "ramda/es/identity";
import includes from "ramda/es/includes";
import prop from "ramda/es/prop";
import propEq from "ramda/es/propEq";
import reduce from "ramda/es/reduce";
import toUpper from "ramda/es/toUpper";
import ltThanLength from "ramda-extension/es/ltThanLength";
import memoizeWithIdentity from "ramda-extension/es/memoizeWithIdentity";
import { createSelector } from 'reselect';
import globalErrors, { REDUCER_NAME } from '../../constants/globalErrors';
const { type, severity, displayType } = globalErrors;
/**
 * get global errors for given form
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getGlobalErrors('formName')(state)
 *
 */ export const getGlobalErrors = memoizeWithIdentity((formName)=>createSelector(path([
        formName,
        REDUCER_NAME
    ]), identity));
const isValidationType = propEq(type.VALIDATION, 'type');
const isFieldDisplayType = propEq(displayType.FIELD, 'displayType');
const isSameFieldName = (fieldName)=>propEq(fieldName, 'fieldName');
const filterPredicate = (severity)=>filter(compose(equals(severity), toUpper, prop('severity')));
export const filterRequired = filterPredicate(severity.REQUIRED);
export const filterError = filterPredicate(severity.ERROR);
export const filterWarn = filterPredicate(severity.WARNING);
const createMessage = (messageFilter)=>compose(reduce((acc, error)=>{
        if (includes(error.message, acc)) {
            return acc;
        }
        return `${acc}${error.message}`;
    }, ''), messageFilter);
const createMsgCond = (filter)=>[
        compose(ltThanLength(0), filter),
        createMessage(filter)
    ];
const createMetaCond = (filter)=>[
        compose(ltThanLength(0), filter),
        compose(flatten, reduce((acc, error)=>{
            if (error.metaInfo) {
                acc.push(error.metaInfo);
            }
            return acc;
        }, []), filter)
    ];
const msgCond = cond([
    createMsgCond(filterRequired),
    createMsgCond(filterError),
    createMsgCond(filterWarn),
    [
        T,
        always(undefined)
    ]
]);
const metaCond = ()=>cond([
        createMetaCond(filterRequired),
        createMetaCond(filterError),
        createMetaCond(filterWarn),
        [
            T,
            always(undefined)
        ]
    ]);
const getFilteredErrors = (fieldName, globalErrors)=>filter(allPass([
        isValidationType,
        isFieldDisplayType,
        isSameFieldName(fieldName)
    ]))(globalErrors);
/**
 * get messages from global errors for field for given form
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getFieldErrorMessages('formName')(state)('fieldName')
 *
 */ export const getFieldErrorMessages = memoizeWithIdentity((formName)=>createSelector([
        getGlobalErrors(formName)
    ], (globalErrors)=>(fieldName)=>{
            const filteredErrors = getFilteredErrors(fieldName, globalErrors);
            return msgCond(filteredErrors);
        }));
/**
 * get meta error from global errors for field for given form
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getFieldErrorMeta('formName')(state)('fieldName')
 *
 */ export const getFieldErrorMeta = memoizeWithIdentity((formName)=>createSelector([
        getGlobalErrors(formName)
    ], (globalErrors)=>(fieldName)=>{
            const filteredErrors = getFilteredErrors(fieldName, globalErrors);
            return metaCond()(filteredErrors);
        }));
